import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// ============================================================================
// Gyártási / nyomtatási várólista — DTO-k a 23-logic.js productionVals
// függvényéből levezetve (17-gyartas.html).
// Logika: printed komponensekre target = minStock + reserved (aktív rendelések
// foglalása), need = max(0, target - stock); urgent = stock<=0 és reserved>0.
// ============================================================================

export interface ProductionTask {
  /** komponens SKU */
  sku: string;
  name: string;
  /** komponens kép fájlnév (components/images/:file) vagy null */
  imageFile: string | null;
  isCustom: boolean;
  /** darab súly gramm */
  weightGrams: number;
  /** nyomtatási idő perc/db */
  printMinutes: number;
  /** aktuális készlet */
  stock: number;
  /** készletminimum */
  minStock: number;
  /** aktív (nem archivált, csomagolatlan) rendelések által lefoglalt db */
  reserved: number;
  /** cél: minStock + reserved */
  target: number;
  /** nyomtatandó: max(0, target - stock) */
  need: number;
  /** stock<=0 és rendelés vár rá */
  urgent: boolean;
  /** stock<=0 */
  out: boolean;
}

/**
 * Egyedi (rendelésre gyártott) tétel. Nem készlet-utántöltés: egy konkrét
 * rendeléshez készül, ezért rendelésszámmal és a webshopban megadott
 * personalizációs szöveggel együtt jelenik meg a gyártási soron.
 */
export interface CustomProductionJob {
  orderProductId: number;
  orderId: number;
  /** rendelésszám a kezelőnek (order_number, hiányában receipt_id) */
  orderNumber: string;
  merchantName: string | null;
  customer: string | null;
  sku: string;
  name: string;
  imageFile: string | null;
  quantity: number;
  /** a vevő által a webshopban megadott personalizációs szöveg */
  personalization: string | null;
  weightGrams: number;
  printMinutes: number;
  orderedAt: string | null;
  deadlineAt: string | null;
  /** ISO időpont, ha már legyártottnak jelölték */
  producedAt: string | null;
}

export interface ProductionQueue {
  tasks: ProductionTask[];
  /** egyedi gyártási feladatok az aktív rendelésekből */
  customJobs: CustomProductionJob[];
  /** összes nyomtatandó db */
  totalPieces: number;
  /** összes nyomtatási idő perc */
  totalMinutes: number;
  /** összes anyagigény gramm */
  totalGrams: number;
  /** még le nem gyártott egyedi darabok */
  customPieces: number;
  customMinutes: number;
  customGrams: number;
}

export interface LogProductionRequest {
  sku: string;
  /** legyártott darabszám — a készletre kerül */
  quantity: number;
}

export interface LogProductionResponse {
  sku: string;
  newStock: number;
}

@Injectable({ providedIn: 'root' })
export class ProductionService {

  constructor(private http: HttpClient) {}

  /** GET production/queue — nyomtatási várólista (rendezés: sürgős, elfogyott, készlet-arány) */
  getQueue(): Observable<ProductionQueue> {
    return this.http.get<ProductionQueue>('production/queue');
  }

  /** POST production/log — legyártott mennyiség rögzítése (készletnövelés) */
  logProduction(request: LogProductionRequest): Observable<LogProductionResponse> {
    return this.http.post<LogProductionResponse>('production/log', request);
  }

  /**
   * POST production/custom/:id/produced — egyedi tétel „legyártva" jelölése
   * vagy a jelölés visszavonása. Készletet nem mozgat.
   */
  setCustomJobProduced(orderProductId: number, done: boolean)
    : Observable<{ orderProductId: number; producedAt: string | null }> {
    return this.http.post<{ orderProductId: number; producedAt: string | null }>(
      `production/custom/${orderProductId}/produced`,
      { done },
    );
  }
}
